import { Col, Form, Input, message, Modal, Row, Switch } from "antd";
import { Rule } from "antd/lib/form";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import React, { useImperativeHandle, useState } from "react";
import { ModalStatus } from "types/modal";
import { bannerApi } from "api/banner.api";
import { Banner } from "types/banner";
import { $url } from "utils/url";

const rules: Rule[] = [{ required: true }];

export interface BannerModal {
  handleCreate: () => void;
  handleUpdate: (Banner: Banner) => void;
}
interface BannerModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

export const BannerModal = React.forwardRef(({ onClose, onSubmitOk }: BannerModalProps, ref) => {
  const [form] = Form.useForm<Banner>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ModalStatus>("create");
  const [selectedBanner, setSelectedBanner] = useState<Banner>();

  useImperativeHandle<any, BannerModal>(
    ref,
    () => ({
      handleCreate() {
        form.resetFields();
        setVisible(true);
        setStatus("create");
        setSelectedBanner(undefined);
      },
      handleUpdate(Banner: Banner) {
        form.setFieldsValue({
          ...Banner,
          position: Number(Banner.position),
          isActive: Boolean(Banner.isActive),
          image: Banner.image ? $url(Banner.image) : undefined,
        });
        setVisible(true);
        setStatus("update");
        setSelectedBanner(Banner);
      },
    }),
    [],
  );

  const getPayload = () => {
    const { ...rest } = form.getFieldsValue();
    return { banner: { ...rest, position: Number(rest.position) } };
  };

  const submitForm = async () => {
    try {
      setLoading(true);
      const valid = await form.validateFields();
      const data = getPayload();
      let res: any = undefined;
      switch (status) {
        case "create":
          res = await bannerApi.create(data);
          message.success("Tạo thành công!");
          break;
        case "update":
          res = await bannerApi.update(selectedBanner?.id || 0, data);
          message.success("Cập nhật thành công!");
          break;
      }
      onSubmitOk();
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose?.();
    setVisible(false);
    setSelectedBanner(undefined);
  };

  return (
    <Modal
      onCancel={() => {
        handleClose();
      }}
      visible={visible}
      title={status == "create" ? "Tạo banner" : "Cập nhật banner"}
      style={{ top: 20 }}
      width={700}
      confirmLoading={loading}
      onOk={submitForm}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Tên banner" name="name" rules={rules}>
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Vị trí"
              name="position"
              rules={[
                ...rules,
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject(new Error("Vị trí phải lớn hơn 0"));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (/[a-zA-Z]/.test(value)) {
                      return Promise.reject(new Error("Vị trí không được nhập kí tự chữ cái"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Hoạt động" name="isActive">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item shouldUpdate={true}>
          {() => {
            return (
              <Form.Item label="Ảnh" name="image">
                <SingleImageUpload
                  onUploadOk={(path: string) => {
                    console.log("onUploadOk:", path);
                    form.setFieldsValue({
                      image: path,
                    });
                  }}
                  imageUrl={form.getFieldValue("image")}
                  uploadUrl={import.meta.env.VITE_API_URL + "/v1/admin/banner/upload"}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
});
